import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConversations } from '../hooks/useConversations';
import { useConversationThread } from '../hooks/useConversationThread';
import { chatService, MESSAGE_MAX_LENGTH, sanitizeMessageBody } from '../services/chat';
import {
  ChatAttachmentType,
  ConversationSummary,
  ShareableItem,
  formatChatRole,
  isArtistRole,
} from '../types/chat';
import './Messages.css';

interface PeerInfo {
  id: string;
  username: string;
  role: string;
  state: string | null;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function previewText(c: ConversationSummary, myId?: string): string {
  if (c.last_message_body) {
    const prefix = c.last_message_sender_id === myId ? 'You: ' : '';
    return `${prefix}${c.last_message_body}`;
  }
  if (c.last_message_attachment_type) {
    const label = c.last_message_attachment_type === 'beat' ? 'a beat' : 'a verse';
    return c.last_message_sender_id === myId ? `You shared ${label}` : `Shared ${label}`;
  }
  return 'Say hi and start the collab';
}

const Messages: React.FC = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const me = authState.user;
  const canChat = isArtistRole(me?.role);

  const { conversations, loading: listLoading, refresh: refreshList } = useConversations(
    !!canChat,
    me?.id
  );
  const { messages, attachments, loading: threadLoading, appendLocal } = useConversationThread(
    conversationId ?? null,
    me?.id
  );

  const [peer, setPeer] = useState<PeerInfo | null>(null);
  const [draft, setDraft] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState<ShareableItem | null>(null);
  const [sharePickerOpen, setSharePickerOpen] = useState(false);
  const [shareItems, setShareItems] = useState<ShareableItem[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [heroReady, setHeroReady] = useState(false);
  const [search, setSearch] = useState('');
  const [dropNotice, setDropNotice] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const startedForRef = useRef<string | null>(null);
  const dropNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dropNoticeTimerRef.current) clearTimeout(dropNoticeTimerRef.current);
    };
  }, []);

  const showDropNotice = useCallback(() => {
    setDropNotice(true);
    if (dropNoticeTimerRef.current) clearTimeout(dropNoticeTimerRef.current);
    dropNoticeTimerRef.current = setTimeout(() => setDropNotice(false), 4000);
  }, []);

  useEffect(() => {
    const t = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Deep link: /messages?to=<artistId>&draft=<text> opens the thread with a
  // starter message ready to edit (used by "Ask for a redo" on submissions).
  const startWith = searchParams.get('to');
  const draftParam = searchParams.get('draft');
  const draftAppliedRef = useRef(false);

  useEffect(() => {
    if (!draftParam || draftAppliedRef.current) return;
    draftAppliedRef.current = true;
    setDraft(sanitizeMessageBody(draftParam));
  }, [draftParam]);

  useEffect(() => {
    if (!startWith || !canChat || startedForRef.current === startWith) return;
    startedForRef.current = startWith;

    let cancelled = false;
    (async () => {
      try {
        const id = await chatService.getOrCreateConversation(startWith);
        if (cancelled) return;
        searchParams.delete('to');
        searchParams.delete('draft');
        setSearchParams(searchParams, { replace: true });
        navigate(`/messages/${id}`, { replace: true });
        refreshList();
      } catch (err) {
        if (cancelled) return;
        setStartError(
          err instanceof Error ? err.message : 'Could not open that conversation'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [startWith, canChat, navigate, refreshList, searchParams, setSearchParams]);

  const activeSummary = useMemo(
    () => conversations.find(c => c.conversation_id === conversationId) || null,
    [conversations, conversationId]
  );

  // Resolve the peer for freshly created threads that aren't in the list yet.
  useEffect(() => {
    if (!conversationId || !me?.id) {
      setPeer(null);
      return;
    }
    if (activeSummary) {
      setPeer({
        id: activeSummary.peer_id,
        username: activeSummary.peer_username,
        role: activeSummary.peer_role,
        state: activeSummary.peer_state,
      });
      return;
    }
    let cancelled = false;
    chatService
      .getConversationPeer(conversationId, me.id)
      .then(result => {
        if (!cancelled) setPeer(result);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [conversationId, activeSummary, me?.id]);

  // Keep the thread pinned to the newest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, conversationId]);

  const openSharePicker = useCallback(async () => {
    setSharePickerOpen(open => !open);
    if (shareItems.length > 0 || !me?.id) return;
    setShareLoading(true);
    try {
      const items = await chatService.getShareableItems(me.id);
      setShareItems(items);
    } catch {
      setShareItems([]);
    } finally {
      setShareLoading(false);
    }
  }, [shareItems.length, me?.id]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationId || !me?.id || sending) return;
    const body = draft.trim();
    if (!body && !pendingAttachment) return;

    setSending(true);
    try {
      const sent = await chatService.sendMessage({
        conversationId,
        senderId: me.id,
        body,
        attachmentType: (pendingAttachment?.type as ChatAttachmentType) || null,
        attachmentId: pendingAttachment?.id || null,
      });
      appendLocal(sent);
      setDraft('');
      setPendingAttachment(null);
      setSharePickerOpen(false);
      // The sidebar picks this up from the shared realtime channel.
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'Message could not be sent');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
    }
  };

  // Chat carries text plus a shared beat or verse — nothing can be dropped in.
  // Blocking dragover as well as drop also stops the browser from navigating
  // away to a file the user dropped by accident.
  const blockDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      blockDrag(e);
      showDropNotice();
    },
    [blockDrag, showDropNotice]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      const hasFile = items ? Array.from(items).some(item => item.kind === 'file') : false;
      if (hasFile || (e.clipboardData?.files?.length ?? 0) > 0) {
        e.preventDefault();
        showDropNotice();
        return;
      }
      const text = e.clipboardData?.getData('text/plain') ?? '';
      const cleaned = sanitizeMessageBody(text);
      if (cleaned !== text) {
        e.preventDefault();
        const target = e.currentTarget;
        const start = target.selectionStart ?? draft.length;
        const end = target.selectionEnd ?? draft.length;
        setDraft(prev => (prev.slice(0, start) + cleaned + prev.slice(end)).slice(0, MESSAGE_MAX_LENGTH));
      }
    },
    [draft.length, showDropNotice]
  );

  const filteredConversations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter(c => c.peer_username.toLowerCase().includes(term));
  }, [conversations, search]);

  if (!canChat) {
    return (
      <div className="messages-page">
        <Helmet>
          <title>DHH Collab | Messages</title>
        </Helmet>
        <div className="messages-page__bg" aria-hidden>
          <span className="messages-page__blob messages-page__blob--1" />
          <span className="messages-page__blob messages-page__blob--2" />
          <span className="messages-page__grid-pattern" />
        </div>
        <div className="messages-page__body messages-page__wrap container-padding">
          <div className="messages-page__gate">
            <h1 className="messages-page__title">Messages</h1>
            <p className="messages-page__gate-text">
              Direct messages are for artists. Set your role to producer, rapper, or both to
              start collabs with other artists.
            </p>
            <Link to={`/profile/${me?.id ?? ''}`} className="messages-page__gate-cta">
              Update my role
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="messages-page"
      onDragEnter={blockDrag}
      onDragOver={blockDrag}
      onDrop={handleDrop}
    >
      <Helmet>
        <title>DHH Collab | Messages</title>
        <meta name="description" content="Chat with producers and rappers to plan your next collab on DHH Collab." />
      </Helmet>

      <div className="messages-page__bg" aria-hidden>
        <span className="messages-page__blob messages-page__blob--1" />
        <span className="messages-page__blob messages-page__blob--2" />
        <span className="messages-page__grid-pattern" />
      </div>

      <section className={`messages-page__hero ${heroReady ? 'messages-page__hero--visible' : ''}`}>
        <div className="messages-page__wrap container-padding">
          <span className="messages-page__eyebrow">Collab</span>
          <h1 className="messages-page__title">Messages</h1>
          <p className="messages-page__subtitle">
            Line up your next track — talk beats, verses, and features with other artists.
          </p>
        </div>
      </section>

      <div className="messages-page__body messages-page__wrap container-padding">
        <div className={`messages-page__layout ${conversationId ? 'messages-page__layout--thread' : ''}`}>
          <aside className="messages-page__list-pane" aria-label="Conversations">
            <div className="messages-page__list-head">
              <h2 className="messages-page__pane-title">Chats</h2>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search artists..."
                className="messages-page__search"
                aria-label="Search conversations"
              />
            </div>

            {listLoading ? (
              <div className="messages-page__list-empty">Loading chats…</div>
            ) : filteredConversations.length === 0 ? (
              <div className="messages-page__list-empty">
                <p>No conversations yet.</p>
                <Link to="/search" className="messages-page__inline-link">
                  Find artists to message
                </Link>
              </div>
            ) : (
              <ul className="messages-page__list">
                {filteredConversations.map(c => (
                  <li key={c.conversation_id}>
                    <Link
                      to={`/messages/${c.conversation_id}`}
                      className={`messages-page__list-item ${
                        c.conversation_id === conversationId ? 'messages-page__list-item--active' : ''
                      }`}
                    >
                      <span className="messages-page__avatar" aria-hidden>
                        {c.peer_username.charAt(0).toUpperCase()}
                      </span>
                      <span className="messages-page__list-body">
                        <span className="messages-page__list-top">
                          <span className="messages-page__list-name">{c.peer_username}</span>
                          <span className="messages-page__list-time">
                            {formatRelative(c.last_message_at)}
                          </span>
                        </span>
                        <span className="messages-page__list-preview">{previewText(c, me?.id)}</span>
                      </span>
                      {c.unread_count > 0 && (
                        <span className="messages-page__unread">{c.unread_count}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <main className="messages-page__thread-pane">
            {!conversationId ? (
              <div className="messages-page__placeholder">
                <span className="messages-page__placeholder-icon" aria-hidden>
                  💬
                </span>
                <p className="messages-page__placeholder-title">Pick a conversation</p>
                <p className="messages-page__placeholder-text">
                  Open a chat on the left, or head to Find Artists and message someone new.
                </p>
              </div>
            ) : (
              <>
                <header className="messages-page__thread-head">
                  <Link to="/messages" className="messages-page__back" aria-label="Back to chats">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </Link>
                  <span className="messages-page__avatar messages-page__avatar--head" aria-hidden>
                    {(peer?.username || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="messages-page__thread-id">
                    <p className="messages-page__thread-name">{peer?.username || 'Conversation'}</p>
                    <p className="messages-page__thread-meta">
                      {[formatChatRole(peer?.role), peer?.state].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {peer && (
                    <Link to={`/profile/${peer.id}`} className="messages-page__thread-profile">
                      View profile
                    </Link>
                  )}
                </header>

                <div className="messages-page__scroll" ref={scrollRef}>
                  {threadLoading && messages.length === 0 ? (
                    <p className="messages-page__thread-status">Loading messages…</p>
                  ) : messages.length === 0 ? (
                    <p className="messages-page__thread-status">
                      No messages yet — send the first one.
                    </p>
                  ) : (
                    messages.map((m, i) => {
                      const mine = m.sender_id === me?.id;
                      const prev = messages[i - 1];
                      const showDay =
                        !prev ||
                        new Date(prev.created_at).toDateString() !==
                          new Date(m.created_at).toDateString();
                      const preview =
                        m.attachment_type && m.attachment_id
                          ? attachments[`${m.attachment_type}:${m.attachment_id}`]
                          : undefined;

                      return (
                        <React.Fragment key={m.id}>
                          {showDay && (
                            <div className="messages-page__day">
                              <span>{formatDayLabel(m.created_at)}</span>
                            </div>
                          )}
                          <div
                            className={`messages-page__bubble-row ${
                              mine ? 'messages-page__bubble-row--mine' : ''
                            }`}
                          >
                            <div
                              className={`messages-page__bubble ${
                                mine ? 'messages-page__bubble--mine' : ''
                              }`}
                            >
                              {preview && (
                                <Link to={preview.href} className="messages-page__attachment">
                                  <span className="messages-page__attachment-kind">
                                    {preview.type === 'beat' ? 'Beat' : 'Verse'}
                                  </span>
                                  <span className="messages-page__attachment-title">
                                    {preview.title}
                                  </span>
                                  <span className="messages-page__attachment-sub">
                                    {preview.subtitle}
                                  </span>
                                </Link>
                              )}
                              {m.body && <p className="messages-page__bubble-text">{m.body}</p>}
                              <span className="messages-page__bubble-time">
                                {formatTime(m.created_at)}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                </div>

                {startError && (
                  <p className="messages-page__error" role="alert">
                    {startError}
                  </p>
                )}

                {sharePickerOpen && (
                  <div className="messages-page__share">
                    <div className="messages-page__share-head">
                      <p className="messages-page__share-title">Share your work</p>
                      <button
                        type="button"
                        className="messages-page__share-close"
                        onClick={() => setSharePickerOpen(false)}
                        aria-label="Close share panel"
                      >
                        ×
                      </button>
                    </div>
                    {shareLoading ? (
                      <p className="messages-page__share-empty">Loading…</p>
                    ) : shareItems.length === 0 ? (
                      <div className="messages-page__share-blank">
                        <p className="messages-page__share-blank-title">
                          You haven't got anything to share yet
                        </p>
                        <p className="messages-page__share-blank-text">
                          Your own beats and verses show up here so you can send them
                          straight into a chat.
                        </p>
                        <div className="messages-page__share-blank-actions">
                          {(me?.role === 'producer' || me?.role === 'producer_rapper') && (
                            <Link to="/upload" className="messages-page__share-blank-cta">
                              Upload a beat
                            </Link>
                          )}
                          {(me?.role === 'rapper' || me?.role === 'producer_rapper') && (
                            <Link to="/cypher" className="messages-page__share-blank-cta">
                              Record a verse
                            </Link>
                          )}
                        </div>
                      </div>
                    ) : (
                      <ul className="messages-page__share-list">
                        {shareItems.map(item => (
                          <li key={`${item.type}:${item.id}`}>
                            <button
                              type="button"
                              className="messages-page__share-item"
                              onClick={() => {
                                setPendingAttachment(item);
                                setSharePickerOpen(false);
                              }}
                            >
                              <span className="messages-page__share-kind">
                                {item.type === 'beat' ? 'Beat' : 'Verse'}
                              </span>
                              <span className="messages-page__share-name">{item.title}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <form className="messages-page__composer" onSubmit={handleSend}>
                  {dropNotice && (
                    <p className="messages-page__drop-notice" role="status">
                      Chat takes text plus your own beats and verses — files can't be
                      dropped or pasted in.
                    </p>
                  )}
                  {pendingAttachment && (
                    <div className="messages-page__chip">
                      <span className="messages-page__chip-kind">
                        {pendingAttachment.type === 'beat' ? 'Beat' : 'Verse'}
                      </span>
                      <span className="messages-page__chip-title">{pendingAttachment.title}</span>
                      <button
                        type="button"
                        className="messages-page__chip-remove"
                        onClick={() => setPendingAttachment(null)}
                        aria-label="Remove attachment"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div className="messages-page__composer-row">
                    <button
                      type="button"
                      className={`messages-page__attach ${
                        sharePickerOpen ? 'messages-page__attach--open' : ''
                      }`}
                      onClick={openSharePicker}
                      aria-label="Share a beat or verse"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zm12-2a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                    <textarea
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      onDragEnter={blockDrag}
                      onDragOver={blockDrag}
                      onDrop={handleDrop}
                      placeholder="Write a message…"
                      className="messages-page__input"
                      rows={1}
                      maxLength={MESSAGE_MAX_LENGTH}
                    />
                    <button
                      type="submit"
                      className="messages-page__send"
                      disabled={sending || (!draft.trim() && !pendingAttachment)}
                    >
                      {sending ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Messages;
